import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, UserX, RotateCcw, Loader2, LogOut, Mail, Calendar, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_STORAGE_KEY = 'cp_admin_token';

function StatusBadge({ disabled }) {
  if (disabled) {
    return <span className="inline-block px-2 py-0.5 text-[10px] rounded-full border bg-red-500/15 text-red-300 border-red-500/30 font-semibold">Désactivé</span>;
  }
  return <span className="inline-block px-2 py-0.5 text-[10px] rounded-full border bg-green-500/15 text-green-300 border-green-500/30 font-semibold">Actif</span>;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

export default function AdminUsers() {
  const [token] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY) || '');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const r = await axios.get(`${API}/admin/users?${params}`, { headers: { 'X-Admin-Token': token } });
      setUsers(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.location.href = '/admin/country-events';
        return;
      }
      setError('Impossible de charger la liste. Réessayez.');
    } finally {
      setLoading(false);
    }
  }, [token, search, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  async function disableUser(u) {
    const total = u.events?.total || 0;
    const msg = total > 0
      ? `Désactiver le compte "${u.email}" ?\n\nCela supprimera définitivement ses ${total} évènement(s) publié(s) (et leurs affiches).\n\nCette action est réversible pour le compte (restaurable), mais les évènements seront perdus.`
      : `Désactiver le compte "${u.email}" ?\n\nCe compte n'a aucun évènement publié. La désactivation est réversible.`;
    if (!window.confirm(msg)) return;
    setBusy(u.id);
    try {
      const r = await axios.delete(`${API}/admin/users/${u.id}`, { headers: { 'X-Admin-Token': token } });
      const deleted = r.data?.events_deleted ?? 0;
      if (deleted > 0) {
        window.alert(`Compte désactivé. ${deleted} évènement(s) supprimé(s).`);
      }
      await fetchUsers();
    } catch (err) {
      window.alert('Erreur lors de la désactivation. Réessayez.');
    } finally { setBusy(''); }
  }

  async function restoreUser(u) {
    if (!window.confirm(`Réactiver le compte "${u.email}" ?`)) return;
    setBusy(u.id);
    try {
      await axios.post(`${API}/admin/users/${u.id}/restore`, {}, { headers: { 'X-Admin-Token': token } });
      await fetchUsers();
    } catch {
      window.alert('Erreur lors de la réactivation.');
    } finally { setBusy(''); }
  }

  if (!token) {
    // Not authenticated → redirect to admin login
    window.location.href = '/admin/country-events';
    return null;
  }

  const counts = {
    total: users.length,
    active: users.filter((u) => !u.disabled).length,
    disabled: users.filter((u) => u.disabled).length,
    totalEvents: users.reduce((s, u) => s + (u.events?.total || 0), 0),
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope']">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/ball-trap-campagne" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="back-to-map">
              <ArrowLeft className="w-4 h-4" /> Carte
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/admin/country-events" className="text-sm text-white/70 hover:text-white" data-testid="goto-events">
              Évènements
            </Link>
            <span className="text-sm text-[#FFC107] font-bold">Utilisateurs</span>
          </div>
          <button
            onClick={() => { localStorage.removeItem(TOKEN_STORAGE_KEY); window.location.href = '/admin/country-events'; }}
            className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-black mb-1">Utilisateurs</h1>
          <p className="text-sm text-[#888]">Gérer les comptes et leurs évènements publiés.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="users-stats">
          <Stat label="Comptes" value={counts.total} color="text-white" />
          <Stat label="Actifs" value={counts.active} color="text-green-300" />
          <Stat label="Désactivés" value={counts.disabled} color="text-red-300" />
          <Stat label="Évènements" value={counts.totalEvents} color="text-[#FFC107]" />
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par email ou nom…"
              className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none text-sm"
              data-testid="users-search"
            />
          </div>
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'Tous' },
              { key: 'active', label: 'Actifs' },
              { key: 'disabled', label: 'Désactivés' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${statusFilter === t.key ? 'bg-[#FFC107] text-black font-bold' : 'bg-white/5 text-[#C8C8C8] hover:bg-white/10'}`}
                data-testid={`users-filter-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-[#888] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : users.length === 0 ? (
          <p className="text-[#888] italic text-sm" data-testid="users-empty">Aucun utilisateur trouvé.</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto rounded-xl border border-white/5 bg-white/[0.02]">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-black/30 text-[#888] text-[11px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">Utilisateur</th>
                    <th className="text-left px-4 py-3">Inscrit le</th>
                    <th className="text-center px-4 py-3">Évènements</th>
                    <th className="text-center px-4 py-3">Statut</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white">{u.name || u.email.split('@')[0]}</div>
                        <div className="text-xs text-[#888] inline-flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#C8C8C8]">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-base font-black text-[#FFC107]">{u.events?.total || 0}</span>
                        {(u.events?.total || 0) > 0 && (
                          <div className="text-[10px] text-[#888] mt-0.5">
                            {u.events.approved}✓ · {u.events.pending}⏳ · {u.events.rejected}✗
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center"><StatusBadge disabled={u.disabled} /></td>
                      <td className="px-4 py-3 text-right">
                        {u.disabled ? (
                          <button onClick={() => restoreUser(u)} disabled={busy === u.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-green-500/15 hover:bg-green-500/25 text-green-200 border border-green-500/30 disabled:opacity-50"
                            data-testid={`restore-btn-${u.id}`}>
                            <RotateCcw className="w-3 h-3" /> Réactiver
                          </button>
                        ) : (
                          <button onClick={() => disableUser(u)} disabled={busy === u.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/20 disabled:opacity-50"
                            data-testid={`disable-btn-${u.id}`}>
                            {busy === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3 h-3" />}
                            Résilier
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {users.map((u) => (
                <div key={u.id} className="rounded-xl bg-white/[0.02] border border-white/5 p-4" data-testid={`user-card-${u.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="font-bold truncate">{u.name || u.email.split('@')[0]}</div>
                      <div className="text-xs text-[#888] truncate">{u.email}</div>
                    </div>
                    <StatusBadge disabled={u.disabled} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#C8C8C8] mb-3">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(u.created_at)}</span>
                    <span><strong className="text-[#FFC107]">{u.events?.total || 0}</strong> évènement(s)</span>
                  </div>
                  {u.disabled ? (
                    <button onClick={() => restoreUser(u)} disabled={busy === u.id}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-green-500/15 hover:bg-green-500/25 text-green-200 border border-green-500/30 disabled:opacity-50">
                      <RotateCcw className="w-3 h-3" /> Réactiver
                    </button>
                  ) : (
                    <button onClick={() => disableUser(u)} disabled={busy === u.id}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/20 disabled:opacity-50">
                      {busy === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      Résilier le compte
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4">
      <div className={`text-2xl font-black ${color}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-[#888] mt-0.5">{label}</div>
    </div>
  );
}
